import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { useOwnedCreator } from '../contexts/OwnedCreatorContext';

import { useSubscription } from '../contexts/SubscriptionContext';

import dataService, { type OutreachCard } from '../services/dataService';
import youtubeService, {
  getUserYouTubeSubscriptionsWithChannel,
  getMyYoutubeUploads,
  startMyChannelScrape,
  addMySponsor,
  getYouTubeOAuthUrl,
} from '../services/youtubeService';
import {
  getInstagramConnectionStatus,
  getMyInstagramMedia,
  startInstagramAnalysis,
  checkAnalysisProgress as checkInstagramProgress,
  getInstagramOAuthUrl,
} from '../services/instagramService';
import { toast } from '../components/ui/shadcn/sonner';
import codeService, { type Code } from '../services/codeService';
import apiConfig from '../config/apiConfig';

import AnalysisProgressBanner, { type BannerSubJob } from '../components/creator/AnalysisProgressBanner';
import { startChannelChecks, updateChannelChecks } from '../services/channelChecks';
import OwnerDashboard from '../components/dashboard/OwnerDashboard';
import { OAUTH_RETURN_KEY } from '../components/dashboard/YouTubeAnalytics';
import type { MerchOwnerHandlers } from '../components/creator/MerchSection';
import type { MerchProduct } from '../services/dataService';
import {
  PlatformInfo, UploadItem, SponsorDeal, buildDealsAndContentIndex, mergeSponsorCodes,
} from '../components/dashboard/sections';
import type { AddSponsorRequest } from '../components/dashboard/SponsorLedger';

/**
 * Dashboard container = the OWNER view of the unified creator surface.
 *
 * Fetches the signed-in creator's REAL data (connected platforms + counts,
 * recent uploads joined to the brands detected in them, sponsors and their
 * codes, the shop, and the deals pipeline) and hands it to
 * <OwnerDashboard/>. The public looked-up creator view (CreatorProfile)
 * composes the SAME shared sections read-only. Never fabricates data —
 * every section degrades to an honest empty state.
 */
const Dashboard: React.FC = () => {
  const owned = useOwnedCreator();
  const { user } = useUser();
  const { getToken } = useAuth();
  const { subscription } = useSubscription();

  const youtubeConnected = typeof window !== 'undefined' && localStorage.getItem('youtube_connected') === 'true';
  const instagramConnected = typeof window !== 'undefined' && localStorage.getItem('instagram_connected') === 'true';

  const [ytSubs, setYtSubs] = useState<number | null>(null);
  const [ytHandle, setYtHandle] = useState<string | null>(null);
  const [igUsername, setIgUsername] = useState<string | null>(null);
  const [igConnected, setIgConnected] = useState<boolean>(instagramConnected);
  const [platformRefreshes, setPlatformRefreshes] = useState<Set<string>>(new Set());
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  // Sponsors come from two records — the sponsorships found in (or added to) the creator's videos,
  // and the codes table — merged into ONE list below (mergeSponsorCodes).
  const [sponsorships, setSponsorships] = useState<SponsorDeal[]>([]);
  const [codes, setCodes] = useState<Code[] | null>(null);
  const [codesRead, setCodesRead] = useState(false);
  const [lastScraped, setLastScraped] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [savingSponsor, setSavingSponsor] = useState(false);
  const [loading, setLoading] = useState(true); // initial dashboard data load
  const [merchProducts, setMerchProducts] = useState<MerchProduct[]>([]);
  const [merchFallbackShops, setMerchFallbackShops] = useState<{ domain: string; url: string }[]>([]);
  const [merchBusy, setMerchBusy] = useState(false);
  // The Deals pipeline, for the Deals-at-a-glance card. null = still reading.
  const [pipeline, setPipeline] = useState<OutreachCard[] | null>(null);
  const [pipelineError, setPipelineError] = useState(false);
  // The creator this user CLAIMED, resolved from the backend claim relationship.
  // Used as the fallback identity when YouTube OAuth localStorage isn't present
  // (a claim made on another device, or a seeded/demo claim) so the owner's own
  // data still loads. YouTube-OAuth state always wins when present.
  const claimedCreator = owned.profile?.creator;
  const [refreshJobs, setRefreshJobs] = useState<BannerSubJob[]>([]);
  const refreshEpoch = useRef(0);
  const finalizedRefresh = useRef<string | null>(null);
  const refreshLock = useRef(false);
  useEffect(() => () => { refreshEpoch.current += 1; }, []);

  const ownHandle = claimedCreator?.handle || ytHandle || '';
  // Platform of "your creator": YouTube whenever OAuth resolved it, else the
  // claimed creator's platform. Drives creator-detail + merch resolution.
  const ownPlatform = claimedCreator?.platform || 'youtube';

  const tier = subscription?.tier;

  // ── Load everything (each source degrades independently to empty) ────────
  const loadAll = useCallback(async () => {
    if (owned.loading) return;
    const token = await getToken().catch(() => null);
    if (!token) { setLoading(false); return; }

    // The Deals pipeline reads alongside everything else and never blocks the page.
    dataService.getOutreachPipeline(token)
      .then(res => { setPipeline(res.cards || []); setPipelineError(false); })
      .catch(() => setPipelineError(true));

    // YouTube channel (real subs + handle). Captured as a promise so we can
    // await it before dropping the loading skeleton — the tiles show real
    // numbers, not a flash of "—" then the value.
    const ytStatusP = localStorage.getItem('youtube_connected') === 'true'
      ? getUserYouTubeSubscriptionsWithChannel(token)
          .then(({ myChannel }) => {
            if (typeof myChannel?.subscriber_count === 'number') setYtSubs(myChannel.subscriber_count);
            if (myChannel?.handle) setYtHandle(myChannel.handle.replace(/^@/, ''));
          })
          .catch(() => { /* keep null → honest empty */ })
      : Promise.resolve();

    // Instagram connection (username; follower count not yet exposed)
    const igStatusP = getInstagramConnectionStatus(token)
      .then((s) => { setIgConnected(!!s.connected); setIgUsername(s.ig_username || null); })
      .catch(() => { /* keep localStorage value */ });

    // Recent uploads (YT + IG) — merged
    const [yt, ig] = await Promise.all([
      localStorage.getItem('youtube_connected') === 'true'
        ? getMyYoutubeUploads(token, 12).catch(() => null) : Promise.resolve(null),
      localStorage.getItem('instagram_connected') === 'true'
        ? getMyInstagramMedia(token, 12).catch(() => null) : Promise.resolve(null),
    ]);

    const rawUploads: UploadItem[] = [];
    for (const v of yt?.videos || []) {
      rawUploads.push({
        id: v.video_id, platform: 'youtube', title: v.title, thumbnail: v.thumbnail_url,
        publishedAt: v.published_at, url: v.url, scraped: v.scraped, brands: [],
      });
    }
    for (const p of ig?.posts || []) {
      rawUploads.push({
        id: p.content_id, platform: 'instagram', title: p.title, thumbnail: p.thumbnail_url,
        publishedAt: p.published_at, url: p.url, scraped: p.scraped, brands: [],
      });
    }

    // Creator detail (own handle) → sponsors + the upload→brand join, via the
    // SAME shared transform the public creator view uses (sections.tsx).
    let detailDeals: SponsorDeal[] = [];
    let brandsByContentId = new Map<string, { name: string; logo?: string | null; color?: string | null }[]>();
    if (ownHandle) {
      try {
        const detail = owned.profile?.creator.handle === ownHandle
          ? owned.profile : await dataService.getCreatorDetail(ownPlatform, ownHandle, { authToken: token });
        if (detail?.creator?.last_scraped) setLastScraped(detail.creator.last_scraped);
        const built = buildDealsAndContentIndex(detail?.sponsorships || []);
        detailDeals = built.deals;
        brandsByContentId = built.brandsByContentId;
        // A saved profile remains useful without a live OAuth connection.
        // Keep saved content distinct from a newly refreshed upload feed.
        if (!rawUploads.length) {
          const seen = new Set<string>();
          for (const sponsor of detail.sponsorships || []) for (const content of sponsor.content_sources || []) {
            if (!content.content_id || seen.has(content.content_id)) continue;
            seen.add(content.content_id);
            rawUploads.push({ id: content.content_id, platform: content.platform || ownPlatform,
              title: content.title || 'Saved content', thumbnail: detail.creator.is_demo ? null : content.thumbnail_url,
              publishedAt: content.published_at, url: detail.creator.is_demo ? '' : content.url || '', scraped: true, brands: [] });
          }
        }
      } catch { /* not scraped yet → no deals; honest empty */ }
    }

    rawUploads.sort((a, b) => (new Date(b.publishedAt || 0).getTime()) - (new Date(a.publishedAt || 0).getTime()));
    // Attach detected brands onto uploads
    for (const u of rawUploads) {
      const b = brandsByContentId.get(u.id);
      if (b && b.length) u.brands = b;
    }
    setUploads(rawUploads);

    // The platform status is awaited so the initial loading skeleton only drops once the
    // channel tiles have their real numbers (or an honest empty).
    await Promise.all([ytStatusP, igStatusP]);
    setSponsorships(detailDeals);
    setLoading(false);
  }, [getToken, ownHandle, ownPlatform, owned.loading, owned.profile]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // The codes on the creator's own channels (what the old Sponsors tab listed). A failed read
  // leaves the sponsorships standing on their own; it never empties the list.
  const loadCodes = useCallback(async () => {
    if (owned.loading) return;
    const channels = owned.profiles.length
      ? owned.profiles.map(({ creator }) => ({ handle: creator.handle, platform: creator.platform }))
      : ownHandle ? [{ handle: ownHandle, platform: ownPlatform }] : [];
    try {
      if (!channels.length) { setCodes([]); return; }
      const token = await getToken();
      if (!token) { setCodes(null); return; }
      const lists = await Promise.all(channels.map(c => codeService.fetchCodes({ channelHandle: c.handle, platform: c.platform, authToken: token })));
      setCodes(Array.from(new Map(lists.flat().map(c => [c.id || c.code, c])).values()));
    } catch {
      setCodes(null);
    } finally {
      setCodesRead(true);
    }
  }, [getToken, owned.loading, owned.profiles, ownHandle, ownPlatform]);

  useEffect(() => { void loadCodes(); }, [loadCodes]);

  const deals = useMemo(() => mergeSponsorCodes(sponsorships, codes), [sponsorships, codes]);

  const handlePlatformRefresh = useCallback(async (platform: string, handle: string) => {
    setPlatformRefreshes(prev => new Set(prev).add(platform));
    try {
      const token = await getToken();
      if (!token) throw new Error('Sign in to check your channels.');
      const result = await dataService.seedCreator(platform, handle, undefined, undefined, token);
      toast(result.message);
      await loadAll();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not request this check.');
    } finally {
      setPlatformRefreshes(prev => { const next = new Set(prev); next.delete(platform); return next; });
    }
  }, [getToken, loadAll]);

  // Each platform reports its own outcome. A successful kickoff is not a completed scan.
  const handleRefresh = useCallback(async () => {
    if (refreshLock.current) return;
    refreshLock.current = true;
    const epoch = ++refreshEpoch.current;
    setRefreshing(true);
    setRefreshJobs([]);
    try {
      const token = await getToken();
      if (!token) throw new Error('Sign in to check your channels.');
      const requests: {platform:string; promise:Promise<{job_id:string}>}[] = [];
      let scheduled = 0;
      const reuseSaved = async (platform: string, handle: string | null) => {
        if (!handle) return false;
        // A status outage isn't permission to run an expensive fallback scan.
        try { await dataService.getCreatorRefreshStatus(platform, handle); }
        catch (error: any) {
          if (error?.status === 404) return false;
          throw error;
        }
        // The server enrolls a saved account if needed and enforces any
        // pause. Never turn "not scheduled yet" into an expensive rescan.
        await dataService.requestCreatorRefresh(platform, handle, token);
        scheduled += 1;
        return true;
      };
      if (youtubeConnected) {
        // Reuse cached analyses; only newly encountered content needs an AI pass.
        if (!await reuseSaved('youtube', ytHandle)) requests.push({platform:'youtube', promise:startMyChannelScrape(token, {max_videos:40, force_reanalyze:false})});
      }
      if (igConnected && igUsername) {
        if (!await reuseSaved('instagram', igUsername)) requests.push({platform:'instagram', promise:startInstagramAnalysis(igUsername, 12, token, true)});
      }
      if (scheduled) toast('Refresh queued. New and changed posts will be analyzed promptly; saved work is reused.');
      if (scheduled && !requests.length) {
        refreshLock.current = false;
        setRefreshing(false);
        await loadAll();
        return;
      }
      if (!requests.length) throw new Error('Connect YouTube or Instagram in Settings to check recent posts.');
      const jobs = await startChannelChecks(requests);
      if (epoch !== refreshEpoch.current) return;
      setRefreshJobs(jobs);
    } catch (error) {
      if (epoch === refreshEpoch.current) {
        refreshLock.current = false;
        setRefreshing(false);
        toast.error(error instanceof Error ? error.message : 'Could not start the check.');
      }
    }
  }, [getToken, youtubeConnected, ytHandle, igConnected, igUsername, loadAll]);

  useEffect(() => {
    if (!refreshJobs.length) return;
    const pending = refreshJobs.filter(job => !job.is_complete);
    if (!pending.length) {
      refreshLock.current = false;
      setRefreshing(false);
      const key = refreshJobs.map(job => job.job_id).join(':');
      if (finalizedRefresh.current !== key) {
        finalizedRefresh.current = key;
        void loadAll();
      }
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const updates = await updateChannelChecks(pending, {
        instagram:checkInstagramProgress,
        youtube:youtubeService.checkAnalysisProgress,
      });
      if (!cancelled) setRefreshJobs(prev => prev.map(job => updates.find(update => update.job_id === job.job_id) || job));
    }, 3000);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [refreshJobs, loadAll]);

  const refreshDisabledReason = !youtubeConnected && !(igConnected && igUsername)
    ? 'Connect YouTube or Instagram in Settings.'
    : null;

  // One "Add sponsor" form, two ways it can be filed. With YouTube connected, it is a sponsorship on
  // the creator's channel (with its videos, and its code filed in the codes table). Without it,
  // the code is filed against the creator's claimed channel with the store it works at — the path
  // the old Sponsors tab used, and the only one that works for an Instagram-only creator.
  const handleAddSponsor = useCallback(async (input: AddSponsorRequest) => {
    setSavingSponsor(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Sign in to add a sponsor.');
      const code = input.code?.trim() || '';
      const store = input.store?.trim() || '';
      if (youtubeConnected) {
        await addMySponsor(token, {
          brand: input.brand, code: code || null, offer: input.offer || null, videos: input.videos,
          website: store ? (/^https?:\/\//i.test(store) ? store : `https://${store}`) : null,
        });
      } else {
        if (!claimedCreator) throw new Error('Connect a channel first, then add its sponsors.');
        if (!code || !store) throw new Error('Add the discount code and the store where it works.');
        const res = await fetch(`${apiConfig.baseUrl}/api/codes/owned`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ platform: claimedCreator.platform, handle: claimedCreator.handle, code,
            brand: input.brand, merchant_domain: store, discount: input.offer || '' }),
        });
        if (!res.ok) throw new Error(res.status === 422
          ? 'Check the code, brand, and store website. Enter a domain such as brand.com.'
          : 'Could not save this sponsor. Please retry.');
      }
      toast.success('Sponsor added.');
      await Promise.all([loadAll(), loadCodes()]);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not add sponsor.');
      throw e; // keep the editor open so the creator's input isn't lost
    } finally {
      setSavingSponsor(false);
    }
  }, [getToken, loadAll, loadCodes, youtubeConnected, claimedCreator]);

  // ── Merch shop (owner-managed here on the dashboard) ─────────────────────
  // Keyed on the creator's canonical YouTube identity (the same one the rest of
  // the dashboard resolves against). The server enforces real ownership on every
  // mutation. Creators with no resolved handle yet can't manage merch until a
  // platform is connected — merchOwner stays undefined and the section hides.
  const loadShop = useCallback(async () => {
    if (!ownHandle) { setMerchProducts([]); setMerchFallbackShops([]); return; }
    try {
      const token = (await getToken()) || undefined;
      const res = await dataService.getCreatorShopItems(ownPlatform, ownHandle, token);
      setMerchProducts(res.items || []);
      setMerchFallbackShops(res.fallback_shops || []);
    } catch { /* best-effort — the section falls back to store links / empty */ }
  }, [ownPlatform, ownHandle, getToken]);

  useEffect(() => { loadShop(); }, [loadShop]);

  const merchOwner = useMemo<MerchOwnerHandlers | undefined>(() => {
    if (!ownHandle) return undefined;
    const run = async (fn: (t?: string) => Promise<unknown>) => {
      setMerchBusy(true);
      try {
        const token = (await getToken()) || undefined;
        await fn(token);
        await loadShop();
      } finally { setMerchBusy(false); }
    };
    return {
      busy: merchBusy,
      onAddShop: async (url: string) => {
        setMerchBusy(true);
        try {
          const token = (await getToken()) || undefined;
          const res = await dataService.addMerchShop(ownPlatform, ownHandle, url, token);
          await loadShop();
          if (!res.items_added) throw new Error(res.message || 'No products found at that link.');
        } finally { setMerchBusy(false); }
      },
      onRemoveShop: (domain) => run(t => dataService.removeMerchShop(ownPlatform, ownHandle, domain, t)),
      onEditItem: (id, fields) => run(t => dataService.editMerchItem(ownPlatform, ownHandle, id, fields, t)),
      onDeleteItem: (id) => run(t => dataService.deleteMerchItem(ownPlatform, ownHandle, id, t)),
    };
  }, [ownPlatform, ownHandle, getToken, loadShop, merchBusy]);

  // TikTok/Twitch have no OAuth flow, but the scrape pipeline tracks any
  // public account — so the workspace lets the creator pin their own handle
  // (stored locally, like the YouTube scope) and shows the scraped stats.
  // Key is per-Clerk-user so a shared browser can't leak one account's
  // linked handles into another's dashboard.
  const linkedHandleKey = useCallback(
    (pid: string) => `bearri_linked_${pid}_handle_${user?.id || 'anon'}`,
    [user?.id],
  );
  // Initialize synchronously from localStorage so linked tiles never flash
  // the "Add by handle" state on load; follower counts hydrate after.
  const [linkedExtras, setLinkedExtras] = useState<Record<string, { handle: string; followers: number | null }>>(() => {
    const out: Record<string, { handle: string; followers: number | null }> = {};
    // user?.id may not be resolved on first render — the loadLinkedExtras
    // effect re-reads with the real key as soon as it is.
    return out;
  });

  const loadLinkedExtras = useCallback(async () => {
    if (!user?.id) return; // wait for the per-user key
    const out: Record<string, { handle: string; followers: number | null }> = {};
    // Synchronous first pass: handles render immediately, stats fill in.
    for (const pid of ['tiktok', 'twitch']) {
      const h = (localStorage.getItem(linkedHandleKey(pid)) || '').trim();
      if (h) out[pid] = { handle: h, followers: null };
    }
    setLinkedExtras(out);
    for (const pid of ['tiktok', 'twitch']) {
      const handle = out[pid]?.handle;
      if (!handle) continue;
      try {
        const token = (await getToken()) || undefined;
        const res = await dataService.getCreatorDetail(pid, handle, { authToken: token });
        out[pid].followers = typeof res.creator?.followers_count === 'number' && res.creator.followers_count > 0
          ? res.creator.followers_count
          : null;
      } catch { /* row may not exist until the first scrape lands — honest '—' */ }
    }
    setLinkedExtras({ ...out });
  }, [getToken, linkedHandleKey, user?.id]);

  useEffect(() => { loadLinkedExtras(); }, [loadLinkedExtras]);

  const handleLinkByHandle = useCallback(async (platform: string, handle: string) => {
    // Canonical: strip @, lowercase — matches the backend's handle
    // normalization so detail lookups can't miss on case.
    const canon = handle.trim().replace(/^@/, '').toLowerCase();
    try {
      const token = (await getToken()) || undefined;
      const res = await dataService.seedCreator(platform, canon, undefined, undefined, token, claimedCreator?.id);
      localStorage.setItem(linkedHandleKey(platform), canon);
      setLinkedExtras(prev => ({ ...prev, [platform]: { handle: canon, followers: null } }));
      toast.success(res.message || `Tracking @${canon} — stats appear once the first scan lands.`);
      loadLinkedExtras();
    } catch (e) {
      toast(e instanceof Error ? e.message : `Could not link ${platform}.`);
      throw e; // keep the tile's input open so the handle isn't lost
    }
  }, [getToken, loadLinkedExtras, linkedHandleKey, claimedCreator?.id]);

  // A channel sheet's "Connect YouTube / Instagram" goes straight to that platform's sign-in and
  // comes back to the same tab (OAuthCallback reads the return path), not to another page first.
  const handleConnect = useCallback(async (platform: string) => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Sign in again to connect.');
      const redirectUri = `${window.location.origin}/oauth-callback`;
      const url = platform === 'youtube' ? await getYouTubeOAuthUrl(redirectUri, token)
        : platform === 'instagram' ? await getInstagramOAuthUrl(redirectUri, token) : '';
      if (!url) throw new Error('No sign-in link came back.');
      try {
        sessionStorage.setItem(OAUTH_RETURN_KEY, platform === 'youtube' ? '/app/dashboard#youtube-analytics' : `/app/dashboard#channel-sheet-${platform}`);
      } catch { /* storage off: the callback lands on the profile */ }
      window.location.assign(url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not start the connection. Try again.');
    }
  }, [getToken]);

  const handleUnlinkHandle = useCallback((platform: string) => {
    localStorage.removeItem(linkedHandleKey(platform));
    setLinkedExtras(prev => {
      const next = { ...prev };
      delete next[platform];
      return next;
    });
  }, [linkedHandleKey]);

  const platforms: PlatformInfo[] = useMemo(() => [
    { id: 'youtube', label: 'YouTube', connected: youtubeConnected, connectable: true,
      saved: claimedCreator?.platform === 'youtube', sample: owned.isDemo,
      followers: ytSubs ?? claimedCreator?.subscriber_count ?? null, handle: ytHandle || (claimedCreator?.platform === 'youtube' ? claimedCreator.handle : null) },
    { id: 'instagram', label: 'Instagram', connected: igConnected, connectable: true, followers: null, handle: igUsername },
    { id: 'tiktok', label: 'TikTok', connected: !!linkedExtras.tiktok, connectable: true, connectVia: 'handle', followers: linkedExtras.tiktok?.followers ?? null, handle: linkedExtras.tiktok?.handle },
    { id: 'twitch', label: 'Twitch', connected: !!linkedExtras.twitch, connectable: true, connectVia: 'handle', followers: linkedExtras.twitch?.followers ?? null, handle: linkedExtras.twitch?.handle },
  ], [youtubeConnected, ytSubs, ytHandle, igConnected, igUsername, linkedExtras, claimedCreator, owned.isDemo]);

  const firstName = claimedCreator?.name || user?.firstName || user?.fullName?.split(' ')[0] || 'Creator';

  return (
    <>
    {refreshJobs.length > 0 && <AnalysisProgressBanner displayName="Your channel check" subJobs={refreshJobs} />}
    <OwnerDashboard
      identity={{ name: firstName, handle: ownHandle, avatarUrl: user?.imageUrl, tier }}
      platforms={platforms}
      uploads={uploads}
      lastScraped={lastScraped}
      refreshing={refreshing}
      refreshDisabledReason={owned.isDemo ? 'Demo snapshot — no live channel is connected.' : refreshDisabledReason}
      onRefresh={handleRefresh}
      onRefreshPlatform={owned.isDemo ? undefined : handlePlatformRefresh}
      onResearchUpdated={() => { void loadAll(); }}
      refreshingPlatforms={platformRefreshes}
      deals={deals}
      savingSponsor={savingSponsor}
      onAddSponsor={handleAddSponsor}
      sponsorNeedsCode={!youtubeConnected}
      onConnect={handleConnect}
      onLinkByHandle={handleLinkByHandle}
      onUnlinkHandle={handleUnlinkHandle}
      merchProducts={merchProducts}
      merchFallbackShops={merchFallbackShops}
      merchOwner={merchOwner}
      loading={loading || owned.loading}
      sponsorsLoading={!codesRead}
      pipeline={pipeline}
      pipelineError={pipelineError}
      hasPitchAccess
      hasProspectAccess
    />
    </>
  );
};

export default Dashboard;
